/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN: any cache, debounce, or timeout used in multiple places—or encoding
 * a product decision—lives here,
 * not as a component-local `const`.
 *
 * These values were duplicated
 * in ~12 files as `5 * 60 * 1000` and `300_000`.
 * Policy changes required every edit
 * and risked divergence. Import here.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/** Milliseconds in a minute. Base for deriving all other durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Minutes a cached datum is considered fresh (offline cache and session). */
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data syncs locally through PowerSync, so a generous `staleTime` (5 min)
 * avoids redundant refetches/re-renders with no real risk of showing stale
 * data. It is also the global `queryClient` default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * Kept as its own constant (although it currently matches the offline cache
 * value) because it is an independent decision: it must stay aligned across
 * `useSession`, the `_app` route guard, and `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/** Milliseconds cap for session verification in route guards. */
const SESSION_CHECK_TIMEOUT_SECONDS = 1.5;

/**
 * Time cap for session verification in route `beforeLoad` guards.
 *
 * P23/CC-60 · I1-bis: lives HERE, and not as a local literal, precisely because
 * the `_app` guard had the cap and `_auth` did not. The defense existed,
 * worked, and never reached `_auth` because it was a loose number inside a
 * function: with `get-session` HUNG (held, neither 401 nor 5xx — those resolve
 * fast) the router would not mount the route and `/login` was left with `#root` EMPTY
 * at 3/6/10/15/20 s, including on the production bundle.
 *
 * Consume it from BOTH guards. If you add a third, consume it as well.
 */
export const SESSION_CHECK_TIMEOUT_MS = SESSION_CHECK_TIMEOUT_SECONDS * MS_PER_SECOND;

/** Seconds before revoking a download's object URL (see constant). */
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Grace period before revoking a `blob:` object URL after triggering a download.
 *
 * It must comfortably exceed the time the browser takes to consume the
 * `<a download>` href; revoking earlier would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
